import type { ConflictDetailsFormData } from '@/features/processo-arbitral/types/conflictTypes';
import type {
  InitializeProcessoArbitralDraftInput,
  ProcessoArbitralDraftListItem,
  ProcessoArbitralDraftRetomadaSource,
  ProcessoArbitralDraftSnapshot,
  ProcessoArbitralDraftStep,
} from '@/features/processo-arbitral/types/rascunhoStepTypes';
import { defaultConflictDetailsFormData } from '@/features/processo-arbitral/utils/conflitoStepUtils';

const cloneConflictData = (value?: Partial<ConflictDetailsFormData>): ConflictDetailsFormData => ({
  ...defaultConflictDetailsFormData,
  ...value,
});

export const createEmptyProcessoArbitralDraftSnapshot = (): ProcessoArbitralDraftSnapshot => ({
  requerentes: [],
  requeridos: [],
  conflito: cloneConflictData(),
});

export const normalizeProcessoArbitralDraftSnapshot = (
  snapshot?: Partial<ProcessoArbitralDraftSnapshot> | null,
): ProcessoArbitralDraftSnapshot => ({
  requerentes: Array.isArray(snapshot?.requerentes) ? snapshot.requerentes : [],
  requeridos: Array.isArray(snapshot?.requeridos) ? snapshot.requeridos : [],
  conflito: cloneConflictData(snapshot?.conflito),
});

export const buildProcessoArbitralDraftUrl = (
  pathname: string,
  draftId?: string | null,
  returnTo?: string | null,
): string => {
  const params = new URLSearchParams();

  if (draftId) {
    params.set('draftId', draftId);
  }

  if (returnTo) {
    params.set('returnTo', returnTo);
  }

  const query = params.toString();
  return query ? `${pathname}?${query}` : pathname;
};

export const getDraftInitializationInput = (
  draftId: string | null,
  ultimoPasso: ProcessoArbitralDraftStep,
): InitializeProcessoArbitralDraftInput => ({
  draftId,
  fallbackToLatest: false,
  createIfMissing: false,
  ultimoPasso,
});

export const getNewDraftInitializationInput = (
  ultimoPasso: ProcessoArbitralDraftStep,
): InitializeProcessoArbitralDraftInput => ({
  draftId: null,
  fallbackToLatest: false,
  createIfMissing: true,
  ultimoPasso,
});

export const getResolvedProcessoArbitralDraftId = (
  draftIdFromQuery: string | null,
  activeDraftId?: string | null,
): string | null => {
  const normalizedDraftIdFromQuery = draftIdFromQuery?.trim();

  if (normalizedDraftIdFromQuery) {
    return normalizedDraftIdFromQuery;
  }

  return activeDraftId ?? null;
};

const DRAFT_STEP_ORDER: ProcessoArbitralDraftStep[] = [
  'requerente',
  'requerido',
  'conflito',
  'documentos',
  'revisao',
];

const DRAFT_STEP_LABELS: Record<ProcessoArbitralDraftStep, string> = {
  requerente: 'Requerente',
  requerido: 'Requerido',
  conflito: 'Conflito',
  documentos: 'Documentos',
  revisao: 'Revisão',
};

const DRAFT_STEP_ROUTES: Record<ProcessoArbitralDraftStep, string> = {
  requerente: '/arbitral/novo/partes/requerente',
  requerido: '/arbitral/novo/partes/requerido',
  conflito: '/arbitral/novo/conflito',
  documentos: '/arbitral/novo/documentos',
  revisao: '/arbitral/novo/revisao',
};

export const PROCESSO_ARBITRAL_DRAFT_STEP_ORDER = Object.keys(
  DRAFT_STEP_ROUTES,
) as ProcessoArbitralDraftStep[];

const isProcessoArbitralDraftStep = (step: string): step is ProcessoArbitralDraftStep =>
  DRAFT_STEP_ORDER.includes(step as ProcessoArbitralDraftStep);

export const avancarUltimoPasso = (
  passoSalvo: ProcessoArbitralDraftStep | null | undefined,
  passoAtual: ProcessoArbitralDraftStep,
): ProcessoArbitralDraftStep =>
  passoSalvo &&
  PROCESSO_ARBITRAL_DRAFT_STEP_ORDER.indexOf(passoSalvo) >
    PROCESSO_ARBITRAL_DRAFT_STEP_ORDER.indexOf(passoAtual)
    ? passoSalvo
    : passoAtual;

export const isProcessoArbitralDraftSnapshotVazio = (
  draft: Pick<
    ProcessoArbitralDraftListItem,
    'totalRequerentes' | 'totalRequeridos' | 'totalDocumentos'
  >,
): boolean =>
  draft.totalRequerentes === 0 && draft.totalRequeridos === 0 && draft.totalDocumentos === 0;

export const resolverPassoDeRetomada = (
  draft: ProcessoArbitralDraftRetomadaSource | null | undefined,
): ProcessoArbitralDraftStep | null => {
  const ultimoPasso = draft?.ultimoPasso ?? null;
  const snapshotVazio = Boolean(draft && isProcessoArbitralDraftSnapshotVazio(draft));

  if (snapshotVazio && ultimoPasso && ultimoPasso !== 'requerente') {
    return 'requerente';
  }

  return ultimoPasso;
};

export const getProcessoArbitralDraftStepRoute = (
  step: ProcessoArbitralDraftStep | string | null | undefined,
): string => {
  const resolvedStep = step ?? 'requerente';
  return isProcessoArbitralDraftStep(resolvedStep)
    ? DRAFT_STEP_ROUTES[resolvedStep]
    : DRAFT_STEP_ROUTES.requerente;
};

export const getProcessoArbitralDraftProgresso = (
  ultimoPasso: ProcessoArbitralDraftStep | string | null | undefined,
): { numero: number; total: number; rotulo: string; percentual: number } => {
  const passo =
    ultimoPasso && isProcessoArbitralDraftStep(ultimoPasso) ? ultimoPasso : 'requerente';
  const numero = DRAFT_STEP_ORDER.indexOf(passo) + 1;
  const total = DRAFT_STEP_ORDER.length;

  return {
    numero,
    total,
    rotulo: DRAFT_STEP_LABELS[passo],
    percentual: Math.round((numero / total) * 100),
  };
};
